/**
 * Unified Playback Pipeline (Sprint 6B — UMXP).
 *
 * Identical lifecycle for Live/VOD, Free/AVOD/SVOD/TVOD/PVOD, and all providers.
 *
 * Viewer Presses Play
 *   → Authorization / Entitlements
 *   → Experience Engine
 *   → Cinematic Intro
 *   → Playback Session Initialization
 *   → Provider Resolution
 *   → Playback Provider
 *   → Media Playback
 *   → Completion
 *   → Analytics / Watch History / Resume / Recommendations / Plugins
 */

export var PipelinePhase = Object.freeze({
  IDLE: "idle",
  PLAY_REQUESTED: "play_requested",
  AUTHORIZATION: "authorization",
  EXPERIENCE: "experience",
  CINEMATIC_INTRO: "cinematic_intro",
  SESSION_INIT: "session_init",
  PROVIDER_RESOLVE: "provider_resolve",
  PROVIDER_READY: "provider_ready",
  MEDIA_PLAYBACK: "media_playback",
  COMPLETION: "completion",
  POST_PLAYBACK: "post_playback",
  ERROR: "error",
  DESTROYED: "destroyed",
});

export var PIPELINE_ORDER = [
  PipelinePhase.IDLE,
  PipelinePhase.PLAY_REQUESTED,
  PipelinePhase.AUTHORIZATION,
  PipelinePhase.EXPERIENCE,
  PipelinePhase.CINEMATIC_INTRO,
  PipelinePhase.SESSION_INIT,
  PipelinePhase.PROVIDER_RESOLVE,
  PipelinePhase.PROVIDER_READY,
  PipelinePhase.MEDIA_PLAYBACK,
  PipelinePhase.COMPLETION,
  PipelinePhase.POST_PLAYBACK,
];

/**
 * @param {object} [options]
 * @param {object} [options.session] PlaybackSession
 * @param {function} [options.onPhase] (phase, meta) => void
 */
export function createPlaybackPipeline(options) {
  options = options || {};
  var phase = PipelinePhase.IDLE;
  var history = [];

  function getPhase() {
    return phase;
  }

  function getHistory() {
    return history.slice();
  }

  function transition(next, meta) {
    if (!next || next === phase) return phase;
    var prev = phase;
    phase = next;
    var entry = {
      previous: prev,
      phase: next,
      at: Date.now(),
      meta: meta || null,
    };
    history.push(entry);
    if (history.length > 80) history.shift();
    if (options.session && typeof options.session.setPipelinePhase === "function") {
      options.session.setPipelinePhase(next);
    }
    if (typeof options.onPhase === "function") {
      try {
        options.onPhase(next, entry);
      } catch (e) {
        /* isolate */
      }
    }
    return phase;
  }

  function reset() {
    phase = PipelinePhase.IDLE;
    history = [];
  }

  /**
   * Run the standard pre-playback gate sequence up to session init.
   * Callers still own async API / Experience resolution.
   */
  function beginPlayRequest(meta) {
    transition(PipelinePhase.PLAY_REQUESTED, meta);
    transition(PipelinePhase.AUTHORIZATION, meta);
    return phase;
  }

  return {
    getPhase: getPhase,
    getHistory: getHistory,
    transition: transition,
    beginPlayRequest: beginPlayRequest,
    reset: reset,
    phases: PipelinePhase,
  };
}

export default createPlaybackPipeline;
